"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

export default function TechStackAccordionForEmbedded() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDark } = useTheme();

  const embeddedStack = {
    platforms:  { title: "MCU Platforms",           color: "green",  items: ["STM32 (ARM Cortex-M0/M3/M4/M7)", "AVR (ATmega, ATtiny)", "RISC-V (ESP32-C3, GD32)", "NXP Kinetis", "Nordic nRF52"] },
    rtos:       { title: "RTOS & Frameworks",        color: "blue",   items: ["FreeRTOS", "Zephyr RTOS", "CMSIS", "STM32 HAL/LL", "Arduino Framework"] },
    protocols:  { title: "Communication Protocols",  color: "purple", items: ["SPI", "I2C", "UART / USART", "CAN Bus", "USB (CDC, HID)", "RS-485"] },
    tools:      { title: "Dev Tools & Testing",      color: "orange", items: ["STM32CubeIDE", "GCC ARM Toolchain", "OpenOCD", "J-Link / ST-Link", "Unity / CMock"] },
  };

  const colorClasses = {
    blue:   "bg-blue-500",
    green:  "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const wrapperBg = isDark ? "bg-zinc-900"   : "bg-[#1a2e1a]";
  const cardBg    = isDark ? "bg-zinc-800"   : "bg-[#0f1f0f]";
  const subText   = isDark ? "text-gray-400" : "text-gray-300";

  return (
    <section
      aria-labelledby="embedded-tech-stack-heading"
      className="w-full max-w-6xl mx-auto p-4 -mt-8 md:-mt-12"
    >
      <h2 id="embedded-tech-stack-heading" className="sr-only">
        Embedded Systems Tech Stack — STM32, ARM Cortex, AVR, RISC-V, FreeRTOS, Zephyr RTOS, SPI, I2C, CAN Bus
      </h2>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="embedded-tech-stack-content"
        className={`w-full ${wrapperBg} border-2 border-green-400 rounded-2xl p-6 flex items-center justify-between group transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.6)]`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 text-xl" aria-hidden="true">
            🔧
          </div>
          <div className="text-left">
            <p className="text-white text-xl md:text-2xl font-semibold">For the technically curious</p>
            <p className={`${subText} text-sm md:text-base mt-1`}>
              Expand to see our full embedded systems tech stack
            </p>
          </div>
        </div>
        <div className="flex-shrink-0" aria-hidden="true">
          <svg className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id="embedded-tech-stack-content"
            role="region"
            aria-labelledby="embedded-tech-stack-heading"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="mt-4 overflow-hidden"
          >
            <motion.div
              className={`${wrapperBg} rounded-2xl p-6 md:p-8`}
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {Object.entries(embeddedStack).map(([key, category]) => (
                  <motion.div
                    key={key}
                    variants={itemVariants}
                    className={`space-y-4 ${cardBg} p-4 rounded-xl hover:shadow-lg hover:shadow-green-400 transition-all`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colorClasses[category.color]}`} aria-hidden="true" />
                      <h3 className="text-white font-semibold text-lg">{category.title}</h3>
                    </div>
                    <ul className="flex flex-wrap gap-x-2 gap-y-1" aria-label={`${category.title} tools`}>
                      {category.items.map((item, i) => (
                        <li key={item} className="text-gray-300 text-sm">
                          {item}{i < category.items.length - 1 ? "," : ""}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="sr-only">
        Our embedded systems tech stack includes: STM32, ARM Cortex-M, AVR, RISC-V, NXP Kinetis, Nordic nRF52 for MCU platforms;
        FreeRTOS, Zephyr RTOS, CMSIS, STM32 HAL/LL, Arduino Framework for RTOS and frameworks;
        SPI, I2C, UART, CAN Bus, USB, RS-485 for communication protocols;
        STM32CubeIDE, GCC ARM Toolchain, OpenOCD, J-Link, Unity, CMock for development tools and testing.
      </p>
    </section>
  );
}